import { apiClient } from '@/shared/services/api';
import type { ApiResponse } from '@/features/auth/services/authApi';

export type ApprovalStatus = "Draft" | "PendingApproval" | "Approved" | "Rejected";

export interface Supplier {
  id: string;
  supplierName: string;
  supplierCode?: string;
  contactPerson?: string;
  phoneNumber?: string;
  email?: string;
  address?: string;
  county?: string;
  town?: string;
  taxId?: string;
  bankAccount?: string;
  mobileMoneyNumber?: string;
  paymentMethod?: string;
  creditLimit?: number;
  creditPeriodDays?: number;
  leadTimeDays?: number;
  isActive: boolean;
  contractStartDate?: string;
  contractEndDate?: string;
  createdAt: string;
  updatedAt?: string;
  /** Maker-checker on supplier creation (#25). */
  approvalStatus?: ApprovalStatus;
  approvedByUserId?: string | null;
  approvedAt?: string | null;
  rejectionReason?: string | null;
}

export interface CreateSupplierRequest {
  supplierName: string;
  supplierCode: string;
  contactPerson: string;
  phoneNumber: string;
  email: string;
  address: string;
  county: string;
  town: string;
  taxId: string;
  bankAccount: string;
  mobileMoneyNumber: string;
  paymentMethod: string;
  creditLimit: number;
  creditPeriodDays: number;
  leadTimeDays: number;
  contractStartDate: string;
  contractEndDate: string;
  notes: string;
}

export interface UpdateSupplierRequest {
  id: string;
  supplierName: string;
  contactPerson: string;
  phoneNumber: string;
  email: string;
  address: string;
  county: string;
  town: string;
  taxId: string;
  bankAccount: string;
  mobileMoneyNumber: string;
  paymentMethod: string;
  creditLimit: number;
  creditPeriodDays: number;
  leadTimeDays: number;
  isActive: boolean;
  contractStartDate: string;
  contractEndDate: string;
  notes: string;
}

export const suppliersApi = {
  getAllSuppliers: async (): Promise<Supplier[]> => {
    const { data } = await apiClient.get<ApiResponse<Supplier[]>>(
      '/Suppliers'
    );
    return data.data;
  },

  getSupplierById: async (id: string): Promise<Supplier> => {
    const { data } = await apiClient.get<ApiResponse<Supplier>>(
      `/Suppliers/${id}`
    );
    return data.data;
  },

  createSupplier: async (
    payload: CreateSupplierRequest
  ): Promise<Supplier> => {
    const { data } = await apiClient.post<ApiResponse<Supplier>>(
      '/Suppliers',
      payload
    );
    return data.data;
  },

  updateSupplier: async (
    id: string,
    payload: UpdateSupplierRequest
  ): Promise<Supplier> => {
    const { data } = await apiClient.put<ApiResponse<Supplier>>(
      `/Suppliers/${id}`,
      payload
    );
    return data.data;
  },

  deleteSupplier: async (id: string): Promise<void> => {
    await apiClient.delete(`/Suppliers/${id}`);
  },
};